import React, { useMemo, useState, useContext, useEffect } from 'react';
import styles from './ClientsContent.module.scss';
import userRowStyles from './UserRow.module.scss';
import UserRow from './UserRow';
import ClientsWorkoutsList from './ClientsWorkoutsList';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import ConnectWithoutContactRoundedIcon from '@mui/icons-material/ConnectWithoutContactRounded';
import {
  getFullName,
  isHighSchool as _isHighSchool,
  canImpersonate,
} from '../../utils';
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import AddClientDialog from './AddClientDialog';
import EditClientDialog from './EditClientDialog';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ListContent from '../composites/ListContent';
import { Product, UserType } from '../../constants';
import {
  useSessionAction,
  useSessionData,
  useUserAction,
} from '../../providers';
import BaseDialog from '../../components/DialogWrappers/BaseDialog';
import EditProgram from '../Programs/EditProgram';
import _ from 'lodash';

import { locales } from '../../constants/locale';

import type { Member, WithOptionalId, AssignedProgram } from '../../types';
import InDepthMember from './InDepthMember';
import { DashboardScrollContext } from '../../screens/Dashboard';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';

const texts = locales.en.components.clients.clientsContent;

enum ContentTabs {
  Summary = 'Summary',
  Workouts = 'Workouts',
}

const getSummaryContentTitles = (isHighSchool: boolean): string[] => {
  return isHighSchool
    ? ['Athlete Name', 'Current Program', 'Trainer', 'Groups', 'Pin', 'Options']
    : [
        'Client Name',
        'Signup Date',
        'Current Program',
        'Trainer',
        'Subscription',
        'Pin',
        'Options',
      ];
};

const getWorkoutsContentTitles = (): string[] => {
  return ['Athlete Name', 'LAST WORKOUT', 'LAST WEEK', 'LAST MONTH', 'Options'];
};

const ClientsContent: React.FC = () => {
  const { gym, loginTrainer, groups, members, trainers, gymProducts } =
    useSessionData();
  const { lookupAccountByEmail, impersonateAccount, isImpersonating } =
    useSessionAction();
  const {
    updateMember,
    createMember,
    createProgram,
    assignProgramAndRefreshMembers,
    updateMemberSubscriptions,
  } = useUserAction();
  const [selectedTab, setSelectedTab] = useState<ContentTabs>(
    ContentTabs.Summary,
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [editedMember, setEditedMember] = useState<Member | null>(null);
  const [editedProgram, setEditedProgram] =
    useState<WithOptionalId<AssignedProgram> | null>(null);
  const [impersonateEmail, setImpersonateEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [selectedMemberForDrillDown, setSelectedMemberForDrillDown] =
    useState<Member | null>(null);
  const { scrollToTop } = useContext(DashboardScrollContext);
  useEffect(() => {
    if (!selectedMemberForDrillDown) {
      return;
    }
    const latestMember = members[selectedMemberForDrillDown.id];
    if (!latestMember) {
      setSelectedMemberForDrillDown(null);
      return;
    }
    if (!_.isEqual(latestMember, selectedMemberForDrillDown)) {
      setSelectedMemberForDrillDown(latestMember);
    }
  }, [members, selectedMemberForDrillDown]);

  const filteredMembers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return Object.values(members)
      .filter((member) => {
        if (!normalizedQuery) {
          return true;
        }
        return getFullName(member).toLowerCase().includes(normalizedQuery);
      })
      .sort(
        (a, b) =>
          `${a.firstName}${a.lastName}`?.localeCompare(b.firstName || '') || -1,
      );
  }, [searchQuery, members]);

  if (!gym) {
    return (
      <EmptyState
        icon={<AccountCircleRoundedIcon />}
        text="No Clients Found For Gym"
      />
    );
  }

  const isHighSchool = _isHighSchool(gym);
  const userType: UserType = isHighSchool ? UserType.Athlete : UserType.Client;

  const handleAddClient = async (
    newMember: WithOptionalId<Member>,
    trainerId?: string,
  ): Promise<boolean> => {
    const success = await createMember(newMember, trainerId);
    if (!success) {
      return false;
    }
    setIsAddClientModalOpen(false);
    return true;
  };

  const handleEditClient = async (
    oldMemberData: Member,
    newMemberData: Member,
    selectedProduct: Product | '',
  ): Promise<boolean> => {
    // Need to first update member data without subscriptions since it can harm subscription if didn't update (like email validation)
    const didUpdate = await updateMember(oldMemberData, newMemberData);
    if (!didUpdate) {
      return false;
    }
    const success = await updateMemberSubscriptions(
      oldMemberData,
      newMemberData,
      selectedProduct,
      gymProducts,
    );
    if (!success) {
      return false;
    }
    setIsEditClientModalOpen(false);
    setEditedMember(null);
    return true;
  };

  const renderAddClientDialog = () => {
    return (
      <AddClientDialog
        userType={userType}
        gymMembers={Object.values(members)}
        trainers={Object.values(trainers).map(({ trainer }) => ({
          id: trainer.id,
          name: getFullName(trainer),
        }))}
        onSubmit={handleAddClient}
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
      />
    );
  };

  const renderEditClientDialog = () => {
    if (!editedMember || !isEditClientModalOpen) return null;
    return (
      <EditClientDialog
        userType={userType}
        trainers={Object.values(trainers).map(({ trainer }) => ({
          id: trainer.id,
          name: getFullName(trainer),
        }))}
        member={editedMember}
        gymMembers={Object.values(members)}
        onSubmit={(newMemberData, newSelectedProduct) =>
          handleEditClient(editedMember, newMemberData, newSelectedProduct)
        }
        isOpen={!!editedMember}
        onClose={() => {
          setIsEditClientModalOpen(false);
          setEditedMember(null);
        }}
      />
    );
  };

  const renderImpersonateDialog = () => {
    const renderImpersonateDialogContent = () => {
      const renderSubmitImpersonateButton = () => {
        return (
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={isImpersonating}
              onClick={async () => {
                if (!impersonateEmail) {
                  setEmailError('Email is required');
                  return;
                }
                const verificationCodeResponse =
                  await lookupAccountByEmail(impersonateEmail);
                if (!verificationCodeResponse?.trainerId) {
                  setEmailError('Email not found');
                  return;
                }

                const didImpersonate = await impersonateAccount(
                  verificationCodeResponse,
                );
                if (!didImpersonate) {
                  setEmailError('Unable to impersonate this email');
                  return;
                }
                setIsImpersonateModalOpen(false);
              }}
            >
              {isImpersonating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Impersonate'
              )}
            </Button>
          </Box>
        );
      };

      return (
        <div
          style={{
            padding: 20,
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <EmailIcon sx={{ marginRight: 2 }} color="primary" />
            <Typography color="primary">Email Address</Typography>
          </Box>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            color="primary"
            placeholder="your@email.com"
            name="email"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '15px',
              },
            }}
            autoComplete="email"
            autoFocus
            value={impersonateEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setImpersonateEmail(e.target.value);
              setEmailError('');
            }}
            error={Boolean(emailError)}
            helperText={emailError ? emailError : ''}
          />
          {renderSubmitImpersonateButton()}
        </div>
      );
    };

    return (
      <BaseDialog
        isOpen={isImpersonateModalOpen}
        onClose={() => setIsImpersonateModalOpen(false)}
        title="Impersonate"
        children={renderImpersonateDialogContent()}
        actions={<div />}
      />
    );
  };

  const handleEditMemberProgram = async (member: Member) => {
    setEditedMember(member);
    const currentProgram = member.program;
    if (!currentProgram?.id) {
      return;
    } else {
      setEditedProgram(currentProgram);
    }
  };

  const handleCreateNewProgram = async (member: Member) => {
    const trainerId = loginTrainer?.id;
    if (!trainerId) return;
    setEditedMember(member);
    setEditedProgram({
      id: undefined,
      name: '',
      trainerId,
      level: 0,
      fitnessGoal: '',
      programItems: [],
      isPrivate: true,
      startDate: new Date().toISOString(),
    });
  };

  const handleMemberSelect = (member: Member) => {
    setSelectedClient(member.id);
    setSelectedMemberForDrillDown(member);
    scrollToTop();
  };

  const renderUserSummaryRow = (data: Member) => {
    return (
      <UserRow
        groups={Object.values(groups)}
        userType={userType}
        archiveMember={async (member) => {
          await handleEditClient(member, { ...member, isActive: false }, '');
        }}
        openEditClientModal={() => {
          setEditedMember(data);
          setIsEditClientModalOpen(true);
        }}
        selected={selectedClient === data.id}
        onSelect={() => handleMemberSelect(data)}
        key={data.id}
        member={data}
        trainer={Object.values(trainers)
          .map(({ trainer }) => trainer)
          .find(({ id }) => data.trainerIds.includes(id))}
        onEditProgram={() => handleEditMemberProgram(data)}
        onCreateNewProgram={() => handleCreateNewProgram(data)}
      />
    );
  };

  const clearEditingProgram = () => {
    setEditedProgram(null);
    setEditedMember(null);
  };

  const handleSubmitEditedProgram = async (
    newProgramData: WithOptionalId<AssignedProgram>,
  ): Promise<boolean> => {
    try {
      if (!editedMember || !editedProgram) return false;
      const trainerId = loginTrainer?.id;
      if (!trainerId) return false;
      const { program, success } = !newProgramData.id
        ? await createProgram(newProgramData, trainerId)
        : { success: true, program: newProgramData };
      const assignableProgram = program as AssignedProgram;
      if (!success) return false;
      if (assignableProgram && editedMember) {
        if (!assignableProgram.startDate) {
          assignableProgram.startDate = new Date().toISOString();
        }
        await assignProgramAndRefreshMembers(
          [editedMember.id],
          assignableProgram,
        );
        clearEditingProgram();
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleDashboardBack = () => {
    if (editedMember && editedProgram) {
      clearEditingProgram();
    } else if (editedMember) {
      clearEditingProgram();
    } else if (selectedMemberForDrillDown) {
      setSelectedMemberForDrillDown(null);
    }
  };

  const getDrillInContent = () => {
    if (editedMember && editedProgram) {
      return (
        <EditProgram
          program={editedProgram}
          onClose={() => clearEditingProgram()}
          onSubmit={handleSubmitEditedProgram}
          proceedAfterSave={true}
        />
      );
    }

    if (selectedMemberForDrillDown) {
      return <InDepthMember member={selectedMemberForDrillDown} />;
    }

    return null;
  };

  const getImpersonateAction = () => {
    return {
      icon: <ConnectWithoutContactRoundedIcon />,
      label: '',
      onClick: () => setIsImpersonateModalOpen(true),
    };
  };

  const getDashboardContentActions = () => {
    const actions = [
      {
        icon: <AccountCircleRoundedIcon />,
        label: `Add ${userType === UserType.Client ? `A Client` : `An Athlete`}`,
        onClick: () => setIsAddClientModalOpen(true),
      },
    ];

    if (canImpersonate(loginTrainer?.id)) {
      actions.push(getImpersonateAction());
    }
    return actions;
  };

  const getSearchPlaceholder = () => {
    if (!filteredMembers.length) return '';
    return `Search for ${userType}s`;
  };

  const renderSummaryContent = () => {
    const renderListName = () => {
      return (
        <span className={styles.sectionTitle}>
          {texts.PER_ATHELETE_METRICS}
        </span>
      );
    };

    return (
      <div className={styles.contentContainer}>
        {renderListName()}
        <ListContent
          items={filteredMembers}
          titles={getSummaryContentTitles(isHighSchool)}
          listHeaderClassName={
            isHighSchool
              ? userRowStyles.athleteListGrid
              : userRowStyles.clientListGrid
          }
          ItemComponent={({ data }) => {
            return renderUserSummaryRow(data);
          }}
          EmptyStateComponent={() => (
            <EmptyState
              icon={<AccountCircleRoundedIcon />}
              cta={{
                icon: <AddBoxOutlinedIcon color="primary" />,
                label: `Add ${userType === UserType.Client ? `A Client` : `An Athlete`}`,
                onClick: () => setIsAddClientModalOpen(true),
              }}
              text="No Clients Have Been Added"
            />
          )}
        />
      </div>
    );
  };

  const renderWorkoutsContent = () => {
    return (
      <ClientsWorkoutsList
        members={filteredMembers}
        groups={Object.values(groups)}
        trainers={Object.values(trainers)}
        userType={userType}
        onArchiveMember={async (member) => {
          await handleEditClient(member, { ...member, isActive: false }, '');
        }}
        onOpenEditClient={(member) => {
          setEditedMember(member);
          setIsEditClientModalOpen(true);
        }}
        onEditProgram={handleEditMemberProgram}
        onCreateNewProgram={handleCreateNewProgram}
        onOpenAddClientDialog={() => setIsAddClientModalOpen(true)}
        titles={getWorkoutsContentTitles()}
        sectionTitle={texts.PER_ATHELETE_METRICS}
        isActive={selectedTab === ContentTabs.Workouts}
      />
    );
  };

  const tabSliderTabs: TabSliderTab[] = [
    {
      label: ContentTabs.Summary,
      content: renderSummaryContent(),
    },
    {
      label: ContentTabs.Workouts,
      content: renderWorkoutsContent(),
    },
  ];

  const renderContent = () => {
    if (editedMember) return null;
    // if (isLoading) {
    //   return (
    //     <div className={styles.loading}>
    //       <CircularProgress />
    //     </div>
    //   );
    // }
    return (
      <>
        <TabSlider
          tabs={tabSliderTabs}
          initialTab={0}
          rightActions={null}
          activeTab={selectedTab === ContentTabs.Summary ? 0 : 1}
          setActiveTab={(idx) =>
            setSelectedTab(tabSliderTabs[idx].label as ContentTabs)
          }
        />
      </>
    );
  };

  const isDrilledIn =
    Boolean(editedMember && editedProgram) ||
    Boolean(selectedMemberForDrillDown);
  const title = editedProgram
    ? `Edit Program ${editedMember?.program?.name}`
    : selectedMemberForDrillDown
      ? `${getFullName(selectedMemberForDrillDown)}`
      : `${userType} List`;

  return (
    <ContentDrill
      title={title}
      onBack={isDrilledIn ? () => handleDashboardBack() : undefined}
      isParent
      actions={getDashboardContentActions()}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
      isDrilledIn={isDrilledIn}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
      {renderAddClientDialog()}
      {renderEditClientDialog()}
      {renderImpersonateDialog()}
    </ContentDrill>
  );
};

export default ClientsContent;
